import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { Confirm } from './../../../Commons/Modals/Confirm';
import {
    GetAdminQuestions,
    SaveAdminQuestion,
    DeleteAdminQuestion
} from './../../../../utilities/BL/QuizzB9Zone';

export function QuizzB9ZoneQuestionsModal({ isOpen, onClose, onQuestionsUpdated }) {
    const { showMessage } = useFloatMessage();
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Editing / Creation mode
    const [editingQuestion, setEditingQuestion] = useState(null); // null when viewing list

    // Confirm modal for deletion
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, eliminar',
        textNo: 'Cancelar'
    });

    useEffect(() => {
        if (isOpen) {
            loadQuestions();
            setEditingQuestion(null);
        }
    }, [isOpen]);

    const loadQuestions = async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminQuestions();
            if (res.success && res.data) {
                setQuestions(res.data);
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al cargar las preguntas.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNew = () => {
        const nextOrder = questions.length + 1;
        setEditingQuestion({
            id: 0,
            instruction: '',
            showLogo: true,
            keepOptionsOrder: false,
            orderIndex: nextOrder,
            timeLimitSeconds: null,
            isActive: true,
            options: [
                { id: 0, text: '', isCorrect: true, orderIndex: 1 },
                { id: 0, text: '', isCorrect: false, orderIndex: 2 },
                { id: 0, text: '', isCorrect: false, orderIndex: 3 },
                { id: 0, text: '', isCorrect: false, orderIndex: 4 }
            ]
        });
    };

    const handleEditQuestion = (q) => {
        setEditingQuestion({
            ...q,
            options: q.options && q.options.length > 0 ? q.options.map(o => ({ ...o })) : [
                { id: 0, text: '', isCorrect: true, orderIndex: 1 },
                { id: 0, text: '', isCorrect: false, orderIndex: 2 }
            ]
        });
    };

    const handleOptionTextChange = (idx, text) => {
        if (!editingQuestion) return;
        const nextOptions = [...editingQuestion.options];
        nextOptions[idx].text = text;
        setEditingQuestion({ ...editingQuestion, options: nextOptions });
    };

    const handleSetCorrectOption = (idx) => {
        if (!editingQuestion) return;
        const nextOptions = editingQuestion.options.map((opt, i) => ({
            ...opt,
            isCorrect: i === idx
        }));
        setEditingQuestion({ ...editingQuestion, options: nextOptions });
    };

    const handleAddOption = () => {
        if (!editingQuestion || editingQuestion.options.length >= 6) return;
        const nextOrder = editingQuestion.options.length + 1;
        const nextOptions = [
            ...editingQuestion.options,
            { id: 0, text: '', isCorrect: false, orderIndex: nextOrder }
        ];
        setEditingQuestion({ ...editingQuestion, options: nextOptions });
    };

    const handleRemoveOption = (idx) => {
        if (!editingQuestion || editingQuestion.options.length <= 2) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Una pregunta debe tener al menos 2 opciones.' });
            return;
        }
        const wasCorrect = editingQuestion.options[idx].isCorrect;
        let nextOptions = editingQuestion.options.filter((_, i) => i !== idx);
        
        // Re-index order
        nextOptions = nextOptions.map((opt, i) => ({ ...opt, orderIndex: i + 1 }));

        // Si se eliminó la correcta, asignar la primera como correcta
        if (wasCorrect && nextOptions.length > 0) {
            nextOptions[0].isCorrect = true;
        }

        setEditingQuestion({ ...editingQuestion, options: nextOptions });
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        if (!editingQuestion.instruction.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'La instrucción o texto de la pregunta es obligatorio.' });
            return;
        }

        const validOptions = editingQuestion.options.filter(o => o.text && o.text.trim().length > 0);
        if (validOptions.length < 2) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Debes capturar al menos 2 opciones con texto válido.' });
            return;
        }

        const hasCorrect = validOptions.some(o => o.isCorrect);
        if (!hasCorrect) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Debes seleccionar una opción correcta.' });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...editingQuestion,
                options: validOptions.map((o, idx) => ({
                    ...o,
                    orderIndex: idx + 1
                }))
            };

            const res = await SaveAdminQuestion(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Pregunta guardada correctamente!'
                });
                setEditingQuestion(null);
                await loadQuestions();
                if (onQuestionsUpdated) onQuestionsUpdated();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar la pregunta.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al procesar la solicitud.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteQuestion = (q) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar la pregunta "${q.instruction}"?`,
            textYes: 'Sí, eliminar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando pregunta...' });
                const res = await DeleteAdminQuestion(q.id);
                if (res.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta eliminada con éxito.' });
                    await loadQuestions();
                    if (onQuestionsUpdated) onQuestionsUpdated();
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al eliminar la pregunta.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    if (!isOpen) return null;

    return (
        <>
            {showConfirm && (
                <Confirm 
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                />
            )}

            <div 
                className="modal fade show d-block" 
                tabIndex="-1" 
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1060 }}
                onClick={(e) => { if (e.target === e.currentTarget && !editingQuestion) onClose(); }}
            >
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content shadow-lg border-0 rounded-4">
                        <div className="modal-header bg-dark text-white border-bottom border-secondary px-4 py-3">
                            <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                                <i className="bi bi-question-circle-fill text-warning"></i>
                                {editingQuestion ? (editingQuestion.id > 0 ? 'Editar Pregunta de Trivia' : 'Nueva Pregunta de Trivia') : 'Gestión de Preguntas y Opciones'}
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close btn-close-white" 
                                aria-label="Close"
                                onClick={() => {
                                    if (editingQuestion) {
                                        setEditingQuestion(null);
                                    } else {
                                        onClose();
                                    }
                                }}
                            ></button>
                        </div>

                        <div className="modal-body p-4 bg-light">
                            {editingQuestion ? (
                                /* Formulario de edición / creación */
                                <form onSubmit={handleSaveQuestion} id="quizzB9QuestionForm">
                                    <div className="card border-0 shadow-sm rounded-3 p-4 bg-white mb-3">
                                        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                            <h6 className="fw-bold text-danger mb-0">
                                                <i className="bi bi-pencil-square me-2"></i>Contenido de la Pregunta
                                            </h6>
                                            <span className="badge bg-secondary">
                                                Orden: #{editingQuestion.orderIndex}
                                            </span>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-semibold text-secondary small">
                                                Texto de la Pregunta / Instrucción *
                                            </label>
                                            <textarea 
                                                className="form-control"
                                                rows="2"
                                                placeholder="Ej. ¿En qué año se fundó el Club Dorados de Sinaloa?"
                                                value={editingQuestion.instruction}
                                                onChange={(e) => setEditingQuestion({ ...editingQuestion, instruction: e.target.value })}
                                                required
                                            ></textarea>
                                        </div>

                                        <div className="row g-3 mb-2">
                                            <div className="col-12 col-md-6">
                                                <div className="form-check form-switch p-2 bg-light rounded border">
                                                    <input 
                                                        className="form-check-input ms-0 me-2" 
                                                        type="checkbox" 
                                                        id="keepOptionsOrderCheck"
                                                        checked={editingQuestion.keepOptionsOrder}
                                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, keepOptionsOrder: e.target.checked })}
                                                    />
                                                    <label className="form-check-label fw-semibold small text-dark" htmlFor="keepOptionsOrderCheck">
                                                        Mantener orden fijo de opciones (No barajar)
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <div className="form-check form-switch p-2 bg-light rounded border">
                                                    <input 
                                                        className="form-check-input ms-0 me-2" 
                                                        type="checkbox" 
                                                        id="isActiveCheck"
                                                        checked={editingQuestion.isActive}
                                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, isActive: e.target.checked })}
                                                    />
                                                    <label className="form-check-label fw-semibold small text-dark" htmlFor="isActiveCheck">
                                                        Pregunta Activa en la Trivia
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Opciones de respuesta */}
                                    <div className="card border-0 shadow-sm rounded-3 p-4 bg-white">
                                        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                            <div>
                                                <h6 className="fw-bold text-danger mb-0">
                                                    <i className="bi bi-ui-radios me-2"></i>Opciones de Respuesta
                                                </h6>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    Marca el selector radial verde de la respuesta que sea la <strong>CORRECTA</strong>.
                                                </small>
                                            </div>
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-danger fw-semibold"
                                                onClick={handleAddOption}
                                                disabled={editingQuestion.options.length >= 6}
                                            >
                                                <i className="bi bi-plus-circle me-1"></i> Agregar Opción
                                            </button>
                                        </div>

                                        <div className="d-flex flex-column gap-3">
                                            {editingQuestion.options.map((opt, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`p-3 rounded-3 border transition-all ${
                                                        opt.isCorrect ? 'border-success bg-success-subtle' : 'border-secondary-subtle bg-light'
                                                    }`}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="form-check mb-0">
                                                            <input 
                                                                className="form-check-input" 
                                                                type="radio" 
                                                                name="correctOptionRadio"
                                                                id={`correctRadio_${idx}`}
                                                                checked={opt.isCorrect}
                                                                onChange={() => handleSetCorrectOption(idx)}
                                                                style={{ width: '22px', height: '22px', cursor: 'pointer' }}
                                                            />
                                                        </div>

                                                        <div className="flex-grow-1">
                                                            <div className="input-group">
                                                                <span className="input-group-text fw-bold text-secondary bg-white">
                                                                    {String.fromCharCode(65 + idx)}
                                                                </span>
                                                                <input 
                                                                    type="text" 
                                                                    className={`form-control ${opt.isCorrect ? 'fw-bold text-success' : ''}`}
                                                                    placeholder={`Texto de la opción ${String.fromCharCode(65 + idx)}...`}
                                                                    value={opt.text}
                                                                    onChange={(e) => handleOptionTextChange(idx, e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>

                                                        {opt.isCorrect ? (
                                                            <span className="badge bg-success px-3 py-2 fw-bold text-white d-none d-md-inline-block">
                                                                <i className="bi bi-check2-circle me-1"></i> CORRECTA
                                                            </span>
                                                        ) : (
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleRemoveOption(idx)}
                                                                title="Eliminar opción"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </form>
                            ) : (
                                /* Listado de Preguntas */
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <p className="small text-muted mb-0">
                                                Total de preguntas configuradas: <strong>{questions.length}</strong> (El aficionado debe responder el 100% de aciertos para ganar un pase dorado).
                                            </p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="btn btn-danger fw-bold d-flex align-items-center gap-2"
                                            onClick={handleCreateNew}
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                            Nueva Pregunta
                                        </button>
                                    </div>

                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-danger" role="status">
                                                <span className="visually-hidden">Cargando preguntas...</span>
                                            </div>
                                        </div>
                                    ) : questions.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {questions.map((q, qIdx) => (
                                                <div key={q.id} className="card border-0 shadow-sm rounded-3 p-3 bg-white">
                                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="badge bg-danger rounded-pill fw-bold fs-6">
                                                                #{qIdx + 1}
                                                            </span>
                                                            <h6 className="fw-bold text-dark mb-0 fs-6">
                                                                {q.instruction}
                                                            </h6>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`badge ${q.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                                {q.isActive ? 'Activa' : 'Inactiva'}
                                                            </span>
                                                            {q.keepOptionsOrder && (
                                                                <span className="badge bg-info text-dark">
                                                                    Orden Fijo
                                                                </span>
                                                            )}
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-primary fw-semibold"
                                                                onClick={() => handleEditQuestion(q)}
                                                            >
                                                                <i className="bi bi-pencil-fill me-1"></i> Editar
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteQuestion(q)}
                                                            >
                                                                <i className="bi bi-trash-fill"></i>
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="row g-2 mt-1">
                                                        {(q.options || []).map((opt, optIdx) => (
                                                            <div key={opt.id || optIdx} className="col-12 col-md-6">
                                                                <div className={`p-2 rounded border small d-flex justify-content-between align-items-center ${
                                                                    opt.isCorrect ? 'bg-success-subtle border-success text-success-emphasis fw-bold' : 'bg-light text-secondary'
                                                                }`}>
                                                                    <span>
                                                                        <strong className="me-2">{String.fromCharCode(65 + optIdx)})</strong>
                                                                        {opt.text}
                                                                    </span>
                                                                    {opt.isCorrect && (
                                                                        <i className="bi bi-check-circle-fill text-success fs-6"></i>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 bg-white rounded-3 border">
                                            <i className="bi bi-question-diamond text-secondary fs-1 mb-2"></i>
                                            <p className="fs-5 fw-bold text-dark mb-1">No hay preguntas registradas</p>
                                            <p className="small text-muted mb-3">Agrega las preguntas de conocimiento del club para la dinámica.</p>
                                            <button 
                                                type="button" 
                                                className="btn btn-danger fw-bold"
                                                onClick={handleCreateNew}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> Crear primera pregunta
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-between">
                            {editingQuestion ? (
                                <>
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary px-4 fw-semibold"
                                        onClick={() => setEditingQuestion(null)}
                                        disabled={isSaving}
                                    >
                                        Volver a la lista
                                    </button>
                                    <button 
                                        type="submit" 
                                        form="quizzB9QuestionForm"
                                        className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle-fill"></i>
                                                Guardar Pregunta
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : (
                                <button 
                                    type="button" 
                                    className="btn btn-outline-secondary px-4 fw-semibold ms-auto"
                                    onClick={onClose}
                                >
                                    Cerrar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default QuizzB9ZoneQuestionsModal;
